<script setup lang="ts">
import type { PrimitiveProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import type { ButtonVariants } from "."
import { Loader2 } from "@lucide/vue"
import { Primitive } from "reka-ui"
import { computed, watchEffect } from "vue"
import { cn } from "@/shared/lib/utils"
import { buttonVariants } from "."

interface Props extends PrimitiveProps {
  variant?: ButtonVariants["variant"]
  size?: ButtonVariants["size"]
  loading?: boolean
  class?: HTMLAttributes["class"]
}

const props = withDefaults(defineProps<Props>(), {
  as: "button",
  loading: false,
})

/** Лише нативний <button> блокує взаємодію через атрибут :disabled. */
const isNativeButton = computed(() => !props.asChild && props.as === "button")

/**
 * На полиморфному корені (as="a", asChild тощо) нативний `disabled` ігнорується
 * браузером, тож loading дизейблимо семантично: aria-disabled + pointer-events-none
 * (+ opacity) з cva-бази, і прибираємо з tab-порядку.
 */
const nonNativeDisabled = computed(
  () => (props.loading && !isNativeButton.value) || undefined,
)

// asChild рендерить РІВНО один child (reka Slot мерджить props у нього). Інжекція
// спінера дала б другий корінь — Slot змерджив би все у спінер, а реальний child
// лишився б сирим. Тому в asChild спінер не рендеримо (контент — на боці child),
// а семантику loading (aria-busy/aria-disabled) все одно мерджимо у child.
if (import.meta.env.DEV) {
  watchEffect(() => {
    if (props.loading && props.asChild) {
      console.warn(
        "[Button] loading-спінер не рендериться в asChild-режимі — child володіє "
        + "власним вмістом. aria-busy/aria-disabled застосовуються до child.",
      )
    }
  })
}
</script>

<template>
  <Primitive
    data-slot="button"
    :data-variant="variant"
    :data-size="size"
    :data-loading="loading || undefined"
    :as="as"
    :as-child="asChild"
    :class="cn(buttonVariants({ variant, size }), props.class)"
    :disabled="(loading && isNativeButton) || undefined"
    :aria-disabled="nonNativeDisabled"
    :aria-busy="loading || undefined"
    :tabindex="nonNativeDisabled ? -1 : undefined"
  >
    <Loader2
      v-if="loading && !asChild"
      class="animate-spin"
      aria-hidden="true"
    />
    <slot />
  </Primitive>
</template>
